import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Icon } from '@ds/core';
import type { ModelosDoBrain, ProvedorDeIa } from '@/data/brain';
import { b } from './classes';
import { useFecharPorFora, useSetasNoMenu } from './ui/camadas';
import { IconeDoProvedor } from './ui/marca';

/**
 * O seletor de modelo — porte do `renderModelPickerMenu` do Brain original. A
 * lista vem de `GET brain/modelos`: só os provedores com chave ligada na
 * plataforma. "Automático" e "Rápido" do topo usam a chave preferida.
 */

export interface EscolhaDeModelo {
    /** `null`: a chave preferida da plataforma. */
    provedor: ProvedorDeIa | null;
    modelo: string;
}

export const ESCOLHA_PADRAO: EscolhaDeModelo = { provedor: null, modelo: 'auto' };

export function nomeDaEscolha(escolha: EscolhaDeModelo, modelos: ModelosDoBrain | null): string {
    if (escolha.provedor) {
        const provedor = modelos?.provedores.find((p) => p.provedor === escolha.provedor);
        const modelo = provedor?.modelos.find((m) => m.id === escolha.modelo);
        if (modelo) return escolha.modelo === 'auto' || escolha.modelo === 'rapido' ? `${provedor?.nome ?? ''} · ${modelo.nome}` : modelo.nome;
    }
    return escolha.modelo === 'rapido' ? 'Rápido' : 'Auto';
}

export interface SeletorDeModeloProps {
    modelos: ModelosDoBrain | null;
    escolha: EscolhaDeModelo;
    aoEscolher: (escolha: EscolhaDeModelo) => void;
}

interface Posicao {
    top: number;
    left: number;
    maxHeight: number;
    acima: boolean;
}

export function SeletorDeModelo({ modelos, escolha, aoEscolher }: SeletorDeModeloProps) {
    const [aberto, setAberto] = useState(false);
    const [posicao, setPosicao] = useState<Posicao | null>(null);
    const botao = useRef<HTMLButtonElement>(null);
    const lista = useRef<HTMLDivElement>(null);
    const setas = useSetasNoMenu();

    const posicionar = useCallback(() => {
        const el = botao.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const folga = 6;
        const borda = 10;
        const largura = 280;
        const preferida = 320;
        const minima = 160;
        const abaixo = window.innerHeight - r.bottom - borda;
        const acimaDisponivel = r.top - borda;
        const acima = abaixo < minima || (abaixo < preferida && acimaDisponivel > abaixo);
        const disponivel = acima ? acimaDisponivel : abaixo;
        let left = r.left;
        if (left + largura > window.innerWidth - borda) left = window.innerWidth - largura - borda;
        setPosicao({ top: acima ? r.top - folga : r.bottom + folga, left: Math.max(borda, left), maxHeight: Math.min(preferida, Math.max(minima, disponivel - folga)), acima });
    }, []);

    const fechar = useCallback(() => {
        setAberto(false);
        setPosicao(null);
    }, []);

    useFecharPorFora(aberto, (alvo) => Boolean(botao.current?.contains(alvo) || lista.current?.contains(alvo)), fechar, botao.current);

    useEffect(() => {
        if (!aberto) return undefined;
        window.addEventListener('resize', posicionar);
        window.addEventListener('scroll', posicionar, true);
        setTimeout(() => lista.current?.querySelector<HTMLElement>('[aria-checked="true"], [role="menuitemradio"]')?.focus(), 0);
        return () => {
            window.removeEventListener('resize', posicionar);
            window.removeEventListener('scroll', posicionar, true);
        };
    }, [aberto, posicionar]);

    const escolher = (proxima: EscolhaDeModelo) => {
        aoEscolher(proxima);
        fechar();
        botao.current?.focus();
    };

    const ativo = (p: ProvedorDeIa | null, m: string) => escolha.provedor === p && escolha.modelo === m;
    const semChave = modelos !== null && modelos.provedores.length === 0;
    const iconeDoBotao = escolha.provedor ?? 'automatico';

    return (
        <div className={b.modelSelectorWrap}>
            <button
                ref={botao} type="button" className={`${b.modelSelector} ${aberto ? b.modelSelectorOpen : ''}`}
                aria-haspopup="menu" aria-expanded={aberto} aria-label={`Modelo: ${nomeDaEscolha(escolha, modelos)}`}
                onClick={(e) => { e.stopPropagation(); if (aberto) fechar(); else { posicionar(); setAberto(true); } }}
            >
                <IconeDoProvedor provedor={iconeDoBotao} tamanho={11} />
                <span className="brain-seletor__nome">{nomeDaEscolha(escolha, modelos)}</span>
                <span style={{ display: 'inline-flex', transform: aberto ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s ease' }}><Icon name="chevron-down" size={10} /></span>
            </button>

            {aberto && posicao && createPortal(
                // eslint-disable-next-line jsx-a11y/interactive-supports-focus -- o foco vai para os itens, não para o contêiner
                <div
                    ref={lista} id="brain-model-picker-dropdown" role="menu" aria-label="Escolher modelo" className={b.modelPickerDropdown} onKeyDown={setas}
                    style={{ top: posicao.top, left: posicao.left, maxHeight: posicao.maxHeight, transform: posicao.acima ? 'translateY(-100%)' : undefined }}
                >
                    <div className={`${b.modelPickerSection} brain-shell__model-picker-smart`}>
                        <div className={b.modelPickerProviderRow}>
                            <IconeDoProvedor provedor="automatico" tamanho={16} />
                            <span className={b.modelPickerProviderLabel}>VecchIA</span>
                        </div>
                        {[
                            { id: 'auto', nome: 'Auto', descricao: 'Escolhe o modelo configurado na plataforma' },
                            { id: 'rapido', nome: 'Rápido', descricao: 'Respostas mais rápidas para pedidos simples' },
                        ].map((m) => (
                            <button key={m.id} type="button" role="menuitemradio" aria-checked={ativo(null, m.id)} className={`${b.modelPickerItem} ${ativo(null, m.id) ? b.modelPickerItemActive : ''}`} onClick={() => escolher({ provedor: null, modelo: m.id })} disabled={semChave}>
                                <IconeDoProvedor provedor="automatico" tamanho={16} />
                                <div className={b.modelPickerItemBody}>
                                    <div className={b.modelPickerItemName}>{m.nome}</div>
                                    <div className={b.modelPickerItemDesc}>{m.descricao}</div>
                                </div>
                                {ativo(null, m.id) && <Icon name="check" size={14} strokeColor="#57534e" />}
                            </button>
                        ))}
                    </div>
                    <div className="brain-shell__model-picker-providers">
                        {modelos === null && <div className={b.modelPickerItemDesc} style={{ padding: '8px 12px' }}>Carregando modelos…</div>}
                        {semChave && <div className={b.modelPickerItemDesc} style={{ padding: '8px 12px' }}>Nenhuma chave de IA está ligada na plataforma.</div>}
                        {modelos?.provedores.map((p, i) => (
                            <Fragment key={p.provedor}>
                                {i > 0 && <div className={b.modelPickerDivider} />}
                                <div className={b.modelPickerSection}>
                                    <div className={b.modelPickerProviderRow}>
                                        <IconeDoProvedor provedor={p.provedor} tamanho={16} />
                                        <span className={b.modelPickerProviderLabel}>{p.nome}</span>
                                    </div>
                                    {p.modelos.map((m) => (
                                        <button key={m.id} type="button" role="menuitemradio" aria-checked={ativo(p.provedor, m.id)} className={`${b.modelPickerItem} ${ativo(p.provedor, m.id) ? b.modelPickerItemActive : ''}`} onClick={() => escolher({ provedor: p.provedor, modelo: m.id })}>
                                            <IconeDoProvedor provedor={p.provedor} tamanho={14} />
                                            <div className={b.modelPickerItemBody}>
                                                <div className={b.modelPickerItemName}>{m.nome}</div>
                                                {m.descricao && <div className={b.modelPickerItemDesc}>{m.descricao}</div>}
                                            </div>
                                            {ativo(p.provedor, m.id) && <Icon name="check" size={14} strokeColor="#57534e" />}
                                        </button>
                                    ))}
                                </div>
                            </Fragment>
                        ))}
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
}
